import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import CustomizedTables from '../../atoms/table';
import { StyledTableCell, StyledTableRow } from '../../atoms/table/tableStyles';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { invokePostFileService, invokeGetService, handleSnackBar, invokePostService } from '../../../store/global/actions'
import DeleteIcon from '@material-ui/icons/Delete';
import AlertDialog from '../../atoms/alertDialog/AlertDialog'
import { getLoggedInUserId } from '../../../store/auth/selectors';
import { IconButton, Tooltip } from '@material-ui/core';
import ReadMoreDialog from '../../atoms/table/ReadMoreDialog';

const useStyles = makeStyles((theme) => ({
    typography: {
        ...theme.typography.button,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(5, 10, 5, 40),
    },
    textCapitalise: {
        textTransform: 'capitalize'
    },
    uploadBox: {
        marginTop: 10
    },
    uploadInput: {
        marginRight: 5
    },
    uploadButton: {
        marginRight: 10
    },
    deleteIcon: {
        cursor: 'pointer'
    }
}));

const PlatformSchema = () => {

    const dispatch = useDispatch()
    const classes = useStyles()
    const loggedInUserId = useSelector(getLoggedInUserId);

    const [schemaFile, setSchemaFile] = useState(null)
    const [validFile, setValidFile] = useState(true)
    const [schemaList, setSchemaList] = useState([])
    const [uploading, setUploading] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfrirmation] = useState(false)
    const [schemaId, setSchemaId] = useState(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadSchemaList()
    }, [])

    const loadSchemaList = () => {

        dispatch(invokeGetService('platformSchemaAvailableVersions'))
            .then(response => {
                setSchemaList(response.data)
            })
    }

    const handleFileUpload = (event) => {
        if (event.target.files[0].type !== 'application/json') {
            setValidFile(false)
            return false
        }

        setValidFile(true)
        setSchemaFile(event.target.files[0])
        console.log(event.target.files[0]);
    };

    /** Upload schema file */
    const uploadSchemaFile = () => {
        let formData = new FormData()
        formData.append('eventTypesFileName', schemaFile.name)
        formData.append('eventTypesFile', schemaFile)
        formData.append('fileType', schemaFile.type)

        setUploading(true)
        dispatch(invokePostFileService('uploadPlatformSchema', formData))
            .then(async (response) => {
                console.log(response)
                setUploading(false)
                if (response.status == true) {
                    loadSchemaList()
                    setSchemaFile(null);
                    dispatch(handleSnackBar({ open: true, message: 'Platform schema file is uploaded successfully', severity: "success" }))
                } else {
                    dispatch(handleSnackBar({ open: true, message: response.message, severity: "error" }))
                }

            }).catch(error => {
                dispatch(handleSnackBar({ open: true, message: 'Could not upload the platform schema', severity: "error" }))
                setUploading(false)
            })
    }

    const headers = [
        { label: 'Version', name: 'version' },
        { label: 'Type', name: 'type' },
        { label: 'Description', name: 'desc' },
        { label: 'Uploaded Date', name: 'uploadedDate' },
        { label: '', name: 'actions', align: 'right' },
    ];

    const convertToLocalTime = (dateString) => {
        let localDate = new Date(dateString);
        let year = localDate.getFullYear();
        let month = String(localDate.getMonth() + 1).padStart(2, '0');
        let day = String(localDate.getDate()).padStart(2, '0');
        let hours = String(localDate.getHours()).padStart(2, '0');
        let minutes = String(localDate.getMinutes()).padStart(2, '0');
        let seconds = String(localDate.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    const deleteSchema = () => {
        setDeleting(true);
        dispatch(invokePostService('deletePlatformSchema', { version: schemaId }))
            .then(response => {
                setDeleting(false);
                dispatch(handleSnackBar({ open: true, message: response.message, severity: response.status ? 'success' : 'error' }))
                loadSchemaList()
                setShowDeleteConfrirmation(false)
            })
    }

    const handleDeleteClick = (schemaVersion) => {
        setSchemaId(schemaVersion)
        setShowDeleteConfrirmation(true)
    }


    return (
        <Grid container direction="row" spacing={2}>

            {showDeleteConfirmation == true && <AlertDialog open={true} onClose={() => { setShowDeleteConfrirmation(false) }} title={"Delete"} content={"Are you sure you want to delete the schema ?"} onProceed={deleteSchema} proceedText={deleting ? "Deleting.." : "Delete"} isLoading={deleting} proceedColor={"secondary"} />}

            {showDeleteConfirmation}

            {!validFile && <Alert seviority={'error'}>Schema must be a json file</Alert>}

            <Grid item xs={12} container className={classes.uploadBox} direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Grid item>
                    <input
                        accept="application/json"
                        id="upload-file-platform-schema"
                        type="file"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        className={classes.uploadInput}
                    />
                    <label htmlFor="upload-file-platform-schema" className={classes.uploadButton}>
                        <Button variant="outlined" color="primary" component="span" title="Click to select file">
                            {schemaFile === null ? 'Choose schema json' : schemaFile.name}
                        </Button>
                    </label>
                    <Button onClick={uploadSchemaFile} variant="contained" color="primary" component="span" disabled={schemaFile === null || uploading == true}>
                        {uploading ? 'Uploading..' : 'Upload Schema'}
                    </Button>
                </Grid>
            </Grid>


            <Grid item xs={12}>
                <CustomizedTables id="platform-schema-table" size="small" headers={headers}>

                    {schemaList.length < 1 ? (
                        <StyledTableRow>
                            <StyledTableCell align={'center'} colSpan={5} component="td" style={{ padding: 15 }}>
                                No schema uploaded.
                            </StyledTableCell>
                        </StyledTableRow>
                    ) : (
                        schemaList.map((item, index) => (
                            <StyledTableRow key={index}>
                                <StyledTableCell align={'left'}>{item.version}</StyledTableCell>
                                <StyledTableCell align={'left'}>
                                    <Tooltip title={item.user_id ? "Custom schema created by blueprint owner" : "Provided and maintained by HyperBlox"}>
                                        <span>
                                            {item.user_id ? "Local" : "System"}
                                        </span>
                                    </Tooltip>
                                </StyledTableCell>
                                <StyledTableCell align={'left'}>
                                    {(item.desc && item.desc !== "") &&
                                        <ReadMoreDialog
                                            triggerLabel={"View"}
                                            title={item.version}
                                            content={item.desc}
                                        />
                                    }
                                </StyledTableCell>
                                <StyledTableCell align={'left'}>{convertToLocalTime(item.updatedAt)}</StyledTableCell>
                                <StyledTableCell align={'right'} style={{ padding: 0, paddingRight: '5px' }}>
                                    {
                                        item.user_id &&
                                        <IconButton size="small" aria-label="more" aria-haspopup="true" onClick={() => handleDeleteClick(item.version)}>
                                            <DeleteIcon
                                                color={'error'}
                                                fontSize="small"
                                            />
                                        </IconButton>
                                    }
                                </StyledTableCell>
                            </StyledTableRow>
                        ))
                    )}

                </CustomizedTables>
            </Grid>

        </Grid>
    );
};

export default PlatformSchema;
